import { Alert, Button, Space, Tag, Typography } from 'antd';
import { HelperLine } from '../ToolList';
import { useNavigate } from 'react-router-dom';
import { useCustom, useGetLocale } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';

const { Text } = Typography;

/**
 * E01 · UnfinishedThoughtTests (portal) — WHAT A CLIENT STARTED AND DID NOT FINISH.
 *
 * Founder, 2026-09-11: *"I want full thing, including unfinished thought tests. This way therapists
 * can see what clients did not finish and work on that on the next sessions."*
 *
 * ─── WHY THIS CANNOT COME FROM THE LEGACY TABLES ───────────────────────────────────────────────
 * It is the clearest thing event sourcing bought this product, and it is worth being precise about.
 * The shipped app writes a `thoughts` row only when the wizard's RESULT step is left — so a person
 * who described a situation, wrote down the thought that frightened them, and then closed the app
 * has left NO ROW ANYWHERE. On the legacy model that work is simply not visible: not to them, not to
 * their therapist, not to anyone. The log has it, because each step was recorded as it happened, and
 * `unfinishedThoughtTests` folds the subject's stream for boundaries with no `OutcomeRated`.
 *
 * So this list shows the therapist something that did not previously exist — the abandoned attempt,
 * and how far it got. That is the session material: the thought someone could not finish testing is
 * usually the one worth sitting with.
 *
 * ─── SCOPE ─────────────────────────────────────────────────────────────────────────────────────
 * READ ONLY, and on purpose. `resumeStep` says where they stopped, which is what a therapist needs
 * to open the conversation; picking the test up mid-flight is the client's own act on their own
 * device, and the board models resuming as theirs. The therapist composing a NEW test for them is
 * the form this list sits above.
 *
 * Empty is the ordinary case — most clients have nothing unfinished, and that is good news, not a missing read. Since
 * D-nav-fixes-2026-10-19 (founder Q-nav-6 A) an empty section is NOT drawn at all (it cost a quarter of the screen above
 * the table), and a non-empty one is ONE line — "Unfinished thought tests: 2 · Pick up at …" — whose Show opens the list.
 * The intro lives behind the list's "?".
 */
export type UnfinishedThoughtTest = {
  thoughtTestId: string;
  activatingEventId: string;
  subject: string;
  description: string;
  automaticThought: string;
  occurredAt: string;
  resumeStep: string;
};

export type UnfinishedThoughtTestsProps = {
  /** The CLIENT, never the therapist. Absent ⇒ the section does not render. */
  subject?: string;
};

/** The eight wizard steps, in the order a person meets them — for "stopped at 3 of 8". */
const STEPS = [
  'activatingEvent',
  'behaviors',
  'automaticThought',
  'evidenceFor',
  'evidenceAgainst',
  'cognitiveDistortions',
  'alternativeThought',
  'outcome',
];

export const UnfinishedThoughtTests = ({
  subject,
}: UnfinishedThoughtTestsProps) => {
  const { t } = useTranslation();
  const locale = useGetLocale();
  const dateLocale = (locale() ?? 'en').startsWith('ru') ? ru : enUS;
  const navigate = useNavigate();

  const { data, isLoading, isError } = useCustom<{
    unfinishedThoughtTests: UnfinishedThoughtTest[];
  }>({
    url: '',
    method: 'get',
    meta: {
      operation: 'unfinishedThoughtTests',
      fields: [
        'thoughtTestId',
        'activatingEventId',
        'subject',
        'description',
        'automaticThought',
        'occurredAt',
        'resumeStep',
      ],
      variables: {
        // the ON-BEHALF handle — whose unfinished work this is. View-gated server-side.
        subject: { name: 'subject', type: 'ID', value: subject },
      },
    },
    queryOptions: { enabled: !!subject },
  });

  if (!subject) return null;

  /**
   * Refine's graphql provider UNWRAPS the operation: `data.data` is the array itself, not
   * `{ unfinishedThoughtTests: [...] }`. Both shapes are read because the wrapped one is what the
   * raw response looks like, and reading only that produced a section that rendered its title and
   * intro over an empty list while the server was returning rows perfectly well.
   */
  const payload = data?.data as unknown;
  const rows: UnfinishedThoughtTest[] = Array.isArray(payload)
    ? (payload as UnfinishedThoughtTest[])
    : (payload as { unfinishedThoughtTests?: UnfinishedThoughtTest[] })
        ?.unfinishedThoughtTests ?? [];

  const started = (at: string) => {
    try {
      // the portal's language, as WhatsNewDrawer does: the RU page showed "Oct 20, 2026, 2:10 AM" (2026-10-20)
      return format(parseISO(at), 'PPp', { locale: dateLocale });
    } catch {
      return at;
    }
  };

  const stepOf = (row: UnfinishedThoughtTest) => {
    const at = STEPS.indexOf(row.resumeStep);
    return t('unfinishedThoughtTests.resumeAt', {
      step: t(`unfinishedThoughtTests.steps.${row.resumeStep}`, {
        defaultValue: row.resumeStep,
      }),
      // 1-based for a human: "step 3 of 8", not "index 2"
      n: at >= 0 ? at + 1 : 1,
      total: STEPS.length,
    });
  };

  if (isError) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 8 }}
        message={t('unfinishedThoughtTests.loadFailed')}
        data-testid="cc-unfinished-thought-tests-error"
      />
    );
  }
  // nothing unfinished (or not known yet): nothing is drawn (Q-nav-6 A)
  if (isLoading || rows.length === 0) return null;

  return (
    <HelperLine
      title={t('unfinishedThoughtTests.title')}
      count={rows.length}
      summary={stepOf(rows[0])}
      testId="cc-unfinished-thought-tests"
    >
      {rows.map((row, i) => (
        <div
          key={row.thoughtTestId}
          data-testid="cc-unfinished-thought-test-row"
          style={{
            borderTop: i === 0 ? undefined : '1px solid #f0f0f0',
            padding: '8px 0',
          }}
        >
          <Text strong>
            {row.automaticThought ||
              row.description ||
              t('unfinishedThoughtTests.untitled')}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Space size={8} wrap>
              <Tag color="orange">{stepOf(row)}</Tag>
              <Text type="secondary">
                {t('unfinishedThoughtTests.started', {
                  when: started(row.occurredAt),
                })}
              </Text>
              <Button
                size="small"
                type="primary"
                ghost
                data-testid="cc-unfinished-thought-test-continue"
                onClick={() =>
                  navigate(
                    `/tools/portalThoughts/create?clientId=${encodeURIComponent(
                      row.subject,
                    )}&thoughtTestId=${encodeURIComponent(
                      row.thoughtTestId,
                    )}`,
                  )
                }
              >
                {t('unfinishedThoughtTests.continue')}
              </Button>
            </Space>
          </div>
          {/* the situation, when the thought itself is what we showed above */}
          {row.automaticThought && row.description && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" italic>
                {row.description}
              </Text>
            </div>
          )}
        </div>
      ))}
    </HelperLine>
  );
};
